/**
 * Eval Persona API Client with React Query Hooks
 *
 * Provides type-safe API client for Eval Persona CRUD using:
 * - Zod for runtime validation
 * - React Query for data fetching and caching
 *
 * Story: 24.1 - Lab Foundation & Eval Personas
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost, apiPut, apiDelete } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// Zod Schemas
// ============================================================================

const EvalPersonaSchema = z.object({
  id: z.number(),
  name: z.string(),
  cefrCode: z.string().nullable(),
  description: z.string(),
  isSystem: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type EvalPersona = z.infer<typeof EvalPersonaSchema>;

// ============================================================================
// Transform Functions
// ============================================================================

function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformSnakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[camelKey] = transformSnakeToCamel(value as Record<string, unknown>);
    } else {
      transformed[camelKey] = value;
    }
  }
  return transformed as T;
}

// ============================================================================
// Fetcher Functions
// ============================================================================

const fetchPersonas = async (): Promise<EvalPersona[]> => {
  try {
    const data = await apiGet<unknown[]>('/api/admin/eval/personas');
    return z
      .array(EvalPersonaSchema)
      .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
  } catch (error) {
    logger.error('Error fetching eval personas:', error);
    throw error;
  }
};

const createPersona = async (input: {
  name: string;
  description: string;
}): Promise<EvalPersona> => {
  try {
    const data = await apiPost<unknown>('/api/admin/eval/personas', input);
    return EvalPersonaSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error creating eval persona:', error);
    throw error;
  }
};

const updatePersona = async (input: {
  id: number;
  name: string;
  description: string;
}): Promise<EvalPersona> => {
  try {
    const { id, ...body } = input;
    const data = await apiPut<unknown>(`/api/admin/eval/personas/${id}`, body);
    return EvalPersonaSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error updating eval persona:', error);
    throw error;
  }
};

const deletePersona = async (id: number): Promise<void> => {
  try {
    await apiDelete(`/api/admin/eval/personas/${id}`);
  } catch (error) {
    logger.error('Error deleting eval persona:', error);
    throw error;
  }
};

// ============================================================================
// React Query Hooks
// ============================================================================

export const useEvalPersonas = () => {
  return useQuery({
    queryKey: ['eval-personas'],
    queryFn: fetchPersonas,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateEvalPersona = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPersona,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-personas'] });
    },
  });
};

export const useUpdateEvalPersona = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePersona,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-personas'] });
    },
  });
};

export const useDeleteEvalPersona = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePersona,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-personas'] });
    },
  });
};
